import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing,
  useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForModel,
  useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing,
  useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromModel,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateModel,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { mapTourTypeDisplayToEnum } from '../utils/tourTypeMapping';

import { ErrorMessageConstants } from '../constants/ErrorMessageConstants';
import ExternalTour from '../model/ExternalTour';

import { usePropertyEditMutation } from './usePropertyEditMutation';

import type {
  GetUserPropertyDetailsFloorPlanUnitWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ExternalTourProps } from '../model/ExternalTour';

type AssociateParams = {
  shouldAssociate?: boolean;
  tourId?: string;
  externalTour?: ExternalTourProps;
};

type UseTourAssociationOptions = {
  listingAlias: string;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
  floorPlanIndex?: number | null;
  unitIndex?: number | null;
};

export const useTourAssociation = ({
  listingAlias,
  listingDetails,
  floorPlanIndex = null,
  unitIndex = null,
}: UseTourAssociationOptions) => {
  const { mutateAsync: associateVrModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyVrModelWebApiHandler_associateModel,
    successToastMessage: 'Tour updated',
    meta: {
      context: {
        api: 'rental-manager-user-property-vr-model',
        details: 'associate VR model',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: associateVrModelListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyVrModelWebApiHandler_associateListing,
    successToastMessage: 'Tour updated',
    meta: {
      context: {
        api: 'rental-manager-user-property-vr-model',
        details: 'associate VR model to listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: addExternalTourForListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing,
    successToastMessage: 'Tour added',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'add external tour for listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: removeExternalTourFromListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing,
    successToastMessage: 'Tour removed',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'remove external tour from listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: addExternalTourForModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForModel,
    successToastMessage: 'Tour added',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'add external tour for model',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: removeExternalTourFromModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromModel,
    successToastMessage: 'Tour removed',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'remove external tour from model',
      },
      invalidates: ['tours'],
    },
  });

  const getModelId = ({
    floorPlanIndex = null,
    unitIndex = null,
    currentProperty: { floorplans, units },
  }: {
    floorPlanIndex?: number | null;
    unitIndex?: number | null;
    currentProperty: {
      floorplans: Array<GetUserPropertyDetailsFloorPlanWebDto>;
      units: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>;
    };
  }): string | null => {
    const floorPlan = floorPlanIndex !== null && floorplans[floorPlanIndex];
    // Unit tour without floor plan
    if (floorPlanIndex === null && unitIndex !== null) {
      return units[unitIndex].id;
    }

    // Unit tour with floor plan
    if (floorPlanIndex !== null && unitIndex !== null) {
      const unit = floorPlan && floorPlan?.units?.[unitIndex];
      return unit ? unit.id : null;
    }
    return floorPlan ? floorPlan.id : null;
  };

  const handleAssociate = ({ shouldAssociate = false, tourId, externalTour }: AssociateParams) => {
    const isFloorPlanOrUnit3dTour = floorPlanIndex !== null || unitIndex !== null;

    const modelId =
      isFloorPlanOrUnit3dTour &&
      listingDetails &&
      getModelId({
        floorPlanIndex,
        currentProperty: { floorplans: listingDetails?.floorPlans || [], units: listingDetails?.units || [] },
        unitIndex,
      });

    if (tourId) {
      if (isFloorPlanOrUnit3dTour) {
        return associateVrModel({
          getParams: {
            vrModelId: Number(tourId),
            listingAlias,
            shouldAssociate: !!shouldAssociate,
            encodedFloorplanKey: (modelId as string) ?? '',
          },
        });
      }
      return associateVrModelListing({
        getParams: { vrModelId: Number(tourId), listingAlias, shouldAssociate: !!shouldAssociate },
      });
    }

    if (externalTour) {
      const tourData = {
        label: externalTour.label,
        tourUrl: externalTour.tourUrl,
        sourceName: externalTour.sourceName,
        tourType: mapTourTypeDisplayToEnum(externalTour.tourType),
        published: true,
      };

      if (isFloorPlanOrUnit3dTour) {
        const modelTourData = {
          ...tourData,
          encodedFloorplanKey: (modelId as string) ?? '',
        };

        if (shouldAssociate) {
          return addExternalTourForModel({
            getParams: { listingAlias, encodedFloorplanKey: (modelId as string) ?? '' },
            postData: modelTourData,
          }).then((result) => ExternalTour(result));
        } else {
          return removeExternalTourFromModel({
            getParams: { listingAlias, encodedFloorplanKey: (modelId as string) ?? '' },
            postData: modelTourData,
          }).then(() => ExternalTour());
        }
      } else {
        if (shouldAssociate) {
          return addExternalTourForListing({
            getParams: { listingAlias },
            postData: tourData,
          }).then((result) => ExternalTour(result));
        } else {
          return removeExternalTourFromListing({
            getParams: { listingAlias },
            postData: tourData,
          }).then(() => ExternalTour());
        }
      }
    }

    return Promise.resolve();
  };

  const handleTourError = (
    error: unknown,
    isMultifamily?: boolean,
    isMultifamilyCustomer?: boolean,
    isSingleFamilyEnterpriseCustomer?: boolean,
  ) => {
    const errorMessage = DatarouterError.getDisplayMessage(error);
    const lowerCaseMessage = errorMessage.toLowerCase();

    if (lowerCaseMessage.includes('invalid url')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL);
    }
    if (lowerCaseMessage.includes('tour already exists')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST);
    }
    if (lowerCaseMessage.includes('is not from a supported provider')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER);
    }
    if (lowerCaseMessage.includes('doesn’t have permission')) {
      return Promise.reject(
        ErrorMessageConstants.EXTERNAL_TOUR_NO_PERMISSION(
          isMultifamily && isMultifamilyCustomer,
          isSingleFamilyEnterpriseCustomer,
        ),
      );
    }
    return Promise.reject('Cannot associate this tour, refresh the page and try again');
  };

  return {
    handleAssociate,
    handleTourError,
  };
};
